"use client";

import { c as _c } from "react/compiler-runtime";
import type { Country, Market } from "@spree/sdk";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import { getStoreName } from "@/lib/store";

/** Country enriched with market info (currency, locale, etc.) */
export interface CountryWithMarket extends Country {
  currency: string;
  default_locale: string;
  marketId: string | null;
}
const storeName = getStoreName();
interface StoreContextValue {
  storeName: string;
  country: string;
  locale: string;
  currency: string;
  countries: CountryWithMarket[];
  /** The Spree markets (with `country_codes`) the store was rendered with. */
  markets: Market[];
  /** The market this domain is pinned to; null if the markets failed to load. */
  market: Market | null;
  loading: boolean;
}
const StoreContext = createContext<StoreContextValue | undefined>(undefined);
interface StoreProviderProps {
  children: ReactNode;
  initialCountry: string;
  initialLocale: string;
  initialMarkets: Market[];
}

/** Build a flat country list from markets, enriching each country with market info. */
function buildCountriesFromMarkets(markets: Market[]): CountryWithMarket[] {
  const seen = new Set<string>();
  const result: CountryWithMarket[] = [];
  for (const market of markets) {
    for (const country of market.countries ?? []) {
      if (seen.has(country.iso)) continue;
      seen.add(country.iso);
      result.push({
        ...country,
        currency: market.currency,
        default_locale: market.default_locale,
        marketId: market.id
      });
    }
  }
  return result;
}

/** Find a country by ISO code in the flat countries list. */
function findCountry(countries: CountryWithMarket[], countryIso: string): CountryWithMarket | undefined {
  return countries.find(c => c.iso.toLowerCase() === countryIso.toLowerCase());
}
export function StoreProvider(t0) {
  const $ = _c(23);
  const {
    children,
    initialCountry,
    initialLocale,
    initialMarkets
  } = t0;
  let t1;
  if ($[0] !== initialMarkets) {
    t1 = buildCountriesFromMarkets(initialMarkets);
    $[0] = initialMarkets;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const countries = t1;
  let t2;
  if ($[2] !== initialMarkets) {
    t2 = initialMarkets.find(_temp) ?? initialMarkets[0];
    $[2] = initialMarkets;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const defaultMarket = t2;
  let t3;
  if ($[4] !== countries || $[5] !== initialCountry) {
    t3 = findCountry(countries, initialCountry);
    $[4] = countries;
    $[5] = initialCountry;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const selectedCountry = t3;
  const t4 = selectedCountry?.iso || initialCountry;
  let t5;
  if ($[7] !== t4) {
    t5 = t4.toLowerCase();
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const country = t5;
  const locale = selectedCountry ? initialLocale : defaultMarket?.default_locale ?? initialLocale;
  const currency = selectedCountry?.currency ?? defaultMarket?.currency ?? "USD";
  let t6;
  if ($[9] !== defaultMarket || $[10] !== initialMarkets || $[11] !== selectedCountry?.marketId) {
    t6 = initialMarkets.find(m_0 => m_0.id === selectedCountry?.marketId) ?? defaultMarket ?? null;
    $[9] = defaultMarket;
    $[10] = initialMarkets;
    $[11] = selectedCountry?.marketId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const market = t6;
  let t7;
  if ($[13] !== countries || $[14] !== country || $[15] !== currency || $[16] !== initialMarkets || $[17] !== locale || $[18] !== market) {
    t7 = {
      storeName,
      country,
      locale,
      currency,
      countries,
      markets: initialMarkets,
      market,
      loading: false
    };
    $[13] = countries;
    $[14] = country;
    $[15] = currency;
    $[16] = initialMarkets;
    $[17] = locale;
    $[18] = market;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const value = t7;
  let t8;
  if ($[20] !== children || $[21] !== value) {
    t8 = <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
    $[20] = children;
    $[21] = value;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp(m) {
  return m.default;
}
export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
}